// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { invokeLoad, stopLoad } from "../../store/auth/action";
import { listTickets, searchTicketIssue, getTicket } from "../../services/ticket.service";
import ActionError from "../errors/ActionError";
import StartLoader from "../loaders/StartLoader";
import TicketTable from "../tables/TicketTable";
import { IoMdClose, IoMdSearch } from "react-icons/io";
import { LuSearch } from "react-icons/lu";
import { ChevronRightIcon, TriangleAlertIcon, DatabaseIcon, CircleHelpIcon } from "../icons";
import SuccessPop from "../popups/SuccessPop";
import { getSession } from "../../utils/sessionstore";
import { MdHistory } from "react-icons/md";
import TransparentButton from "../buttons/TransparentButton";
import GeneralButton from "../buttons/GeneralButton";

// `priority` matches the new stored value; `legacyPriority` covers tickets raised
// before the switch from High/Medium/Low, so their pill still highlights correctly.
const IMPACT_OPTIONS = [
  { key: "blocked", label: "Cannot Proceed", icon: DatabaseIcon, priority: "Cannot Proceed", legacyPriority: "High" },
  { key: "slowed", label: "Slowed me down", icon: TriangleAlertIcon, priority: "Slowed me down", legacyPriority: "Medium" },
  { key: "note", label: "Just a note (FYI)", icon: CircleHelpIcon, priority: "Just a note (FYI)", legacyPriority: "Low" },
];

const StatusBadge = ({ status }) => {
  const isClosed = status === "Closed";
  return (
    <div
      className={`shrink-0 px-1.5 py-0.5 rounded-lg flex justify-center items-center ${
        isClosed ? "bg-green-50 text-success" : "bg-[#FEF3C7] text-[#B45309]"
      }`}
    >
      <span className="text-center text-[11px] font-medium leading-4">{status || "Open"}</span>
    </div>
  );
};

// Minimizing the Help & Support panel swaps to a completely different bottom-bar
// layout, which unmounts PanelTicketDetail; re-expanding remounts it. Cache each
// ticket's data here so toggling minimize/expand doesn't re-trigger a visible
// loading flash for a ticket we already fetched this session.
const ticketDetailCache = new Map();

// New-design ticket detail view, fetching the same real data as the shared
// TicketDetail popup (getTicket) but styled to match the sidepanel/new UI.
// Rendered by the outer Help & Support panel as its own "ticketDetail" view,
// so back-navigation is handled by the panel's existing header/breadcrumb,
// matching the original design (no extra back button inside this view).
export const PanelTicketDetail = ({ ticketId }) => {
  const [isLoading, setIsLoading] = useState(!ticketDetailCache.has(ticketId));
  const [failure, setFailure] = useState(false);
  const [ticket, setTicket] = useState(() => ticketDetailCache.get(ticketId) || null);

  useEffect(() => {
    if (ticketDetailCache.has(ticketId)) {
      setTicket(ticketDetailCache.get(ticketId));
      setIsLoading(false);
      return;
    }

    async function load() {
      try {
        setIsLoading(true);
        const response = await getTicket(ticketId);
        ticketDetailCache.set(ticketId, response);
        setTicket(response);
      } catch (e) {
        setFailure(true);
      } finally {
        setIsLoading(false);
      }
    }
    if (ticketId) load();
  }, [ticketId]);

  return (
    <div className="self-stretch flex-1 min-h-0 flex flex-col justify-start items-start">
      {isLoading && <StartLoader />}
      {failure && <ActionError />}

      {ticket && !isLoading && (
        <div className="self-stretch flex-1 min-h-0 p-3 flex flex-col justify-start items-start gap-3.5 overflow-y-auto">
          <div className="self-stretch flex flex-col justify-start items-start gap-1">
            <div className="text-general-foreground text-xs font-normal leading-4">
              Dashboard Name
            </div>
            <div className="self-stretch min-h-8 px-2.5 py-1.5 bg-general-muted rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-general-border flex justify-start items-center">
              <span className="text-general-foreground text-xs font-normal leading-4">
                {ticket?.feature || "—"}
              </span>
            </div>
          </div>

          <div className="self-stretch flex flex-col justify-center items-start gap-1.5">
            <div className="text-general-foreground text-xs font-normal leading-4 whitespace-nowrap">
              How much is this affecting you?
            </div>
            <div className="self-stretch inline-flex justify-start items-center gap-1.5 flex-wrap content-center">
              {IMPACT_OPTIONS.map((option) => {
                const Icon = option.icon;
                const selected = option.priority === ticket?.priority || option.legacyPriority === ticket?.priority;
                return (
                  <div
                    key={option.key}
                    className={`min-h-7 px-1.5 py-1 rounded-md outline outline-1 outline-offset-[-1px] flex justify-center items-center gap-1 text-xs font-normal leading-4 text-general-foreground ${
                      selected ? "outline-general-primary bg-general-accent" : "bg-general-muted outline-general-border"
                    }`}
                  >
                    <Icon className="size-3.5" />
                    {option.label}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="self-stretch flex flex-col justify-start items-start gap-1 overflow-hidden">
            <div className="text-general-foreground text-xs font-normal leading-4">
              Details
            </div>
            <div className="self-stretch h-36 rounded-lg outline outline-1 outline-offset-[-1px] outline-general-border flex flex-col justify-start items-start overflow-hidden">
              <div className="self-stretch flex-1 p-3 bg-general-muted shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] text-left text-general-foreground text-sm font-normal leading-5 overflow-y-auto">
                {ticket?.detail || "—"}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ViewTickets = ({ searchedResult, setSearchedResult, onGoBack, isPopup, variant = "page", onViewTicket }) => {
  const { success, isLoading, failure, userDetails, clientname } = useSelector(
    (state) => ({
      success: state.success,
      isLoading: state.isLoading,
      userDetails: state.userDetails,
      clientname: state.clientname,
    })
  );

  const [searchedIssue, setSearchedIssue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [panelLoading, setPanelLoading] = useState(
    variant === "panel" && (!searchedResult || searchedResult.length === 0)
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();

  async function getSearchDetail(searchterm) {
    if (searchterm.length > 0) {
      const response = await searchTicketIssue(searchterm);

      setSearchedResult(response.tickets);
    } else {
      populateTicketVeiw();
    }
    setCurrentPage(1);
  }

  function getSearchedIssues() {
    const token = getSession("session_hwai");
    if (!token) {
      navigate("/login");
    } else {
      if (variant === "panel") setPanelLoading(true);
      getSearchDetail(searchedIssue).finally(() => {
        if (variant === "panel") setPanelLoading(false);
      });
    }
  }

  async function populateTicketVeiw() {
    const response = await listTickets();
    setSearchedResult(response.tickets);
  }

  useEffect(() => {
    const token = getSession("session_hwai");
    if (!token) {
      navigate("/login");
    } else if (variant === "panel") {
      // The panel context has its own local loading state (below) instead of the
      // app-global isLoading/success flags, which other already-mounted pages
      // (e.g. the dashboard behind this panel) also react to.
      // searchedResult is lifted to the parent panel, so it already survives this
      // component unmounting when you navigate to ticket detail and back - only
      // fetch here if it's genuinely empty, so going back doesn't reload the list.
      if (!searchedResult || searchedResult.length === 0) {
        setPanelLoading(true);
        populateTicketVeiw().finally(() => setPanelLoading(false));
      }
    } else {
      dispatch(invokeLoad());
      populateTicketVeiw();
      dispatch(stopLoad());
    }
  }, []);

  if (variant === "panel") {
    return (
      <div className="self-stretch flex-1 min-h-0 flex flex-col justify-start items-start">
        <div className="self-stretch p-3">
          <div className="relative self-stretch">
            <LuSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-general-muted-foreground pointer-events-none" />
            <input
              placeholder="Search tickets..."
              onChange={(e) => setSearchedIssue(e.target.value)}
              value={searchedIssue}
              onKeyDown={(e) => e.key === "Enter" && getSearchedIssues()}
              className="self-stretch w-full min-h-8 pl-8 pr-7 py-1.5 bg-general-background rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-general-border text-general-foreground placeholder:text-general-muted-foreground text-xs font-normal leading-4 ani"
            />
            {searchedIssue && (
              <button
                type="button"
                onClick={() => {
                  setSearchedIssue("");
                  populateTicketVeiw();
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-general-muted-foreground hover:text-general-foreground ani"
              >
                <IoMdClose className="size-3.5" />
              </button>
            )}
          </div>
        </div>

        <div className="self-stretch flex-1 min-h-0 px-3 pb-3 flex flex-col justify-start items-start gap-2 overflow-y-auto">
          {(searchedResult || []).length === 0 && !panelLoading && (
            <div className="self-stretch text-center text-general-muted-foreground text-xs font-normal leading-4 py-4">
              No tickets yet.
            </div>
          )}
          {(searchedResult || []).map((ticket) => (
            <div
              key={ticket._id}
              className="self-stretch p-2.5 rounded-lg outline outline-1 outline-offset-[-1px] outline-general-border hover:bg-[#FAFAFA] ani flex flex-col justify-start items-start gap-1.5"
            >
              <div className="self-stretch inline-flex justify-start items-start gap-2">
                <div className="flex-1 min-w-0 min-h-8 line-clamp-2 text-left text-general-foreground text-xs font-medium leading-4">
                  {ticket?.detail || ticket?.feature || "Untitled ticket"}
                </div>
                <StatusBadge status={ticket?.status} />
              </div>
              <div className="self-stretch inline-flex justify-start items-center gap-2">
                <div className="flex-1 min-w-0 text-left text-general-muted-foreground text-[11px] font-normal leading-4">
                  {ticket?.customTicketId || ticket?._id} · {ticket?.raisedDate?.slice(0, 10)}
                </div>
                <button
                  type="button"
                  onClick={() => onViewTicket?.(ticket._id)}
                  className="min-h-5 px-1.5 py-0.5 bg-general-input rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-general-border flex justify-center items-center gap-1 ani hover:bg-[#FAFAFA]"
                >
                  <span className="text-center text-general-foreground text-[11px] font-medium leading-4">
                    View details
                  </span>
                  <ChevronRightIcon className="size-2.5 text-general-foreground" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {isPopup && (
          <div className="self-stretch p-3 border-t border-general-border">
            <button
              type="button"
              onClick={onGoBack}
              className="w-full min-h-8 px-3 py-1.5 bg-general-input rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-general-border flex justify-center items-center gap-2 ani hover:bg-general-accent"
            >
              <span className="text-center text-general-foreground text-xs font-medium leading-4">Hide History</span>
            </button>
          </div>
        )}

        {failure && <ActionError />}
      </div>
    );
  }

  return (
    <>
  <div className="w-full px-[32px] py-[28px] text-left">
    {/* Heading  View Ticket */}
    <div className="flex flex-col mb-[24px] gap-[8px]">
      <div className="flex items-center gap-[16px]">
        <MdHistory className="font-extrabold text-2xl" />
        <div className="Roboto text-zinc-600 text-base font-semibold leading-tight tracking-wide">
          Ticket History
        </div>
      </div>
    </div>
    {/* Heading end  View Ticket */}

    <div className="flex flex-col gap-4">
      {/* table */}
      <div className="flex justify-start items-center Roboto gap-[8px]">
        <div className="relative w-full">
          <div className="absolute inset-y-0 left-0 flex items-center pl-[8px] pointer-events-none">
            <IoMdSearch className="text-zinc-300 h-4 w-4" />
          </div>
          <input
            placeholder="Search..."
            onChange={(e) => setSearchedIssue(e.target.value)}
            value={searchedIssue}
            className="border border-zinc-300 pl-8 pr-[8px] py-[4px] w-full text-[13px] text-zinc-800 leading-tight tracking-wide placeholder:text-zinc-300 focus:outline-none"
          />
          {searchedIssue && (
            <div
              className="absolute inset-y-0 right-0 flex items-center pr-[8px] cursor-pointer"
              onClick={() => setSearchedIssue("")}
            >
              <IoMdClose className="text-zinc-800 h-4 w-4" />
            </div>
          )}
        </div>

        <TransparentButton text={"Search"} onClick={getSearchedIssues} />
      </div>

      <div className="border-[1px] border-zinc-300 px-[8px] pb-[8px]">
        <TicketTable
          data={searchedResult}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </div>
      {isPopup && (
        <div className="flex justify-start">
          <GeneralButton text={"Hide History"} onClick={onGoBack} />
        </div>
      )}
    </div>
  </div>

  {success && <SuccessPop />}
  {isLoading && <StartLoader />}
  {failure && <ActionError />}
</>
  );
};

export default memo(ViewTickets);
